/** Show whole credits, preserving the distinction between a small balance and zero. */

export function wholeCredits(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n));
}

export function formatCreditBalance(value) {
  const n = Number(value);
  if (n > 0 && n < 1) return '<1';
  return wholeCredits(value).toLocaleString();
}

/** Compact balance without relying on native Intl compact-notation support. */
export function formatCompactCreditBalance(value) {
  const n = Number(value);
  if (n > 0 && n < 1) return '<1';
  let amount = wholeCredits(value), unit = 0;
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  while (amount >= 1000 && unit < suffixes.length - 1) { amount /= 1000; unit++; }
  // Rounding near a unit boundary should show 1M rather than 1000K.
  if (unit && Number(amount.toFixed(1)) >= 1000 && unit < suffixes.length - 1) { amount /= 1000; unit++; }
  return unit ? `${amount.toFixed(1).replace(/\.0$/, '')}${suffixes[unit]}` : String(amount);
}

/**
 * The full-turn estimate and initial hold are independent. File tool rounds
 * can cost more than the amount required to start the first model call.
 */
export function resolveComposerCreditQuote(estimate, hold) {
  const estimated = Number.isFinite(Number(estimate)) ? Math.max(0, Number(estimate)) : 0;
  const hasHold = hold != null && Number.isFinite(Number(hold));
  const reserved = hasHold ? Math.max(0, Number(hold)) : estimated;
  return {
    estimated,
    reserved,
    hasHold: hasHold && reserved > 0,
  };
}

export function composerCreditCalculation(cost) {
  return {
    // The server's tariff already includes context increments and options.
    // Keep it separate from both predicted usage and the balance required.
    cost: cost?.total ?? null,
    estimatedCost: cost?.estimatedTotal ?? (cost?.variableBilling ? cost?.authorizationTotal : cost?.total)
      ?? cost?.total ?? cost?.authorizationTotal ?? null,
    authorizationCost: cost?.authorizationTotal ?? cost?.total ?? null,
    variableBilling: !!cost?.variableBilling,
    fileEstimate: !!cost?.fileEstimate,
  };
}

/**
 * Rewrite millicredit / float-error amounts in user-facing copy so a stored
 * ask_user card never shows 43653.797770700001.
 */
export function formatCreditsInText(text) {
  return String(text ?? '').replace(
    /\d+\.\d+(?=\s*credits\b)|\d+\.\d{3,}/gi,
    (raw) => formatCreditBalance(raw),
  );
}
